import { html, css, LitElement } from 'lit-element';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { clone, cloneDeep } from 'lodash-es';
import { library, dom } from "@fortawesome/fontawesome-svg-core";   
 
import { getStorage, ref, getDownloadURL, uploadBytes, deleteObject } from "firebase/storage";

import { emitEvent, hasRole } from './utility.js';
import { t, tHtml, Localized, formatDate, LOCALES } from './i18n.js';
import { fetchStorageJson } from './utility.js';
import { applyDomainOverlay, extractDomainOverlay, domainOverlayPath } from './domains.js';
import { storage } from './intrivity_app.js';

import '@material/mwc-textfield';

export const DOMAIN_EDITOR_CANCEL = "Intrivity::DomainEditor::Cancel";
export const DOMAIN_EDITOR_SAVE = "Intrivity::DomainEditor::Save";
export const DOMAIN_EDITOR_PUBLISH = "Intrivity::DomainEditor::Publish";
export const DOMAIN_EDITOR_RELEASE = "Intrivity::DomainEditor::Release";

// Edits one domain in one language. `locale` 'en' edits the English file
// itself; any other locale edits that language's overlay (see domains.js):
// the page shows the English file with the translation applied, and Publish
// writes back only the translated text. Each language has its own checkout,
// so English and a translation can be edited at the same time.
export class IntrivityDomainEditor extends Localized(LitElement) {

    get _isTranslation() { return !!this.locale && this.locale !== 'en'; }
    get _basePath() { return 'domains/domain_' + this.domain_name + '.json'; }
    get _checkoutPath() {
        return 'domains/' + this.domain_name + (this._isTranslation ? '.' + this.locale : '') + '_editor';
    }
    get _publishPath() { return this._isTranslation ? domainOverlayPath(this.domain_name, this.locale) : this._basePath; }

    // No checkout exists yet: start one from the published version.
    async _startCheckout() {
        const base = await fetchStorageJson(this._basePath);
        if (!base) {
            this.editor = false;
            alert(t('domainEditor.loadFailed'));
            return;
        }
        let data = base;
        if (this._isTranslation) {
            const overlay = await fetchStorageJson(this._publishPath);
            // Round-trip through JSON to drop applyDomainOverlay's hidden
            // reference to the English file.
            data = JSON.parse(JSON.stringify(applyDomainOverlay(base, overlay)));
            data.published = (overlay && overlay.published) || [];
        }
        data.editor = this.user_profile.email;
        data.checked_out = new Date().toISOString();
        this.putJSONInStorage(this._checkoutPath, data);
    }

    // check if local editing version... 
    // if not.. check if stored editing version...
    // if not load from current version
    getJSONFromStorage(file_name) {
        try {
            let val = localStorage.getItem(file_name);
            if (val) {
                this.original = JSON.parse(val);
                this.domain = cloneDeep(this.original);
            } else {
                this.loadJSONFromStorage(file_name);
            }
        } catch (e) {
            console.log(e);
        }
    }    
    loadJSONFromStorage(file_name) {
        const storageRef = ref(storage, file_name);
        getDownloadURL(storageRef)
            .then(url => fetch(url))
            .then(response => response.json())
            .then(data => { 
                if (file_name === this._checkoutPath) {
                    // check if WE are the editor
                    if (data.editor && data.editor === this.user_profile.email) {
                        localStorage.setItem(file_name, JSON.stringify(data));
                        this.original = data;
                        this.domain = cloneDeep(this.original);
                        this.editor = true;
                    } else {
                        this.editor = false;
                        this.original = data;
                    }
                }
                this.requestUpdate(); 
            }).catch(reason => {
                console.log(reason);
                if (file_name === this._checkoutPath) {
                    this._startCheckout();
                } else {
                    this.editor = false;
                    alert(t('domainEditor.loadFailed'));
                }
            });
    }
    putJSONInStorage = async function(name, json, save, publish) {
        var data = JSON.stringify(json);
        const blob = new Blob([data], { type: "application/json" });

        const storageRef = ref(storage, name);
        uploadBytes(storageRef, blob).then((snapshot) => {
            if (!publish) localStorage.setItem(name, data);         
            this.original = json;
            this.domain = cloneDeep(this.original);
            this.editor = true;  
            emitEvent(DOMAIN_EDITOR_SAVE, {}, this);
            this.requestUpdate();
            if (save) { alert(t('domainEditor.saved')); }
            if (publish) { 
                alert(t('domainEditor.published'));
                this.editor = false;
                this.deleteFromStorage(this._checkoutPath, true);
            }
        }).catch(reason => {
            console.log(reason);
            if (save) { 
                alert(t('domainEditor.saveFailed')); }         
            else if (publish) { 
                alert(t('domainEditor.publishFailed')); 
            } else {
                this.editor = false;
            }
        }); 
        
    }
    deleteFromStorage = async function(name, release) {
        const storageRef = ref(storage, name);
        deleteObject(storageRef).then(() => {
            if (release) {
                localStorage.removeItem(name);
                this.editor = false;
                this.domain = undefined;
                emitEvent(DOMAIN_EDITOR_RELEASE, {}, this);
                this.requestUpdate();
                alert(t('domainEditor.released'));
            }
        }).catch((error) => {
            console.log(error);
            alert(t('domainEditor.deleteFailed', { name }));
        });  

    }
    
    hasChanges() {
        return !(JSON.stringify(this.domain) === JSON.stringify(this.original));
    }
    
    connectedCallback() {
        super.connectedCallback();
    }
    
    firstUpdated() {
        this.getJSONFromStorage(this._checkoutPath);
    }
    
    cancelChanges(e) {
        if (this.hasChanges()) {
            this.domain = cloneDeep(this.original);
            emitEvent(DOMAIN_EDITOR_CANCEL, {}, this);
            this.requestUpdate();
            alert(t('domainEditor.reverted'));
        } else {
            alert(t('domainEditor.nothingToCancel'));
        }        
    }
    
    saveChanges(e) {
        if (this.hasChanges()) {
            if (!this.domain.saved) {
                this.domain.saved = [];
            }
            let editlog = {};
            editlog.editor = this.domain.editor;
            editlog.checked_out = this.domain.checked_out;
            editlog.saved = new Date();
            this.domain.saved.push(editlog);
            localStorage.setItem(this._checkoutPath, JSON.stringify(this.domain));
            this.putJSONInStorage(this._checkoutPath, this.domain, true);
            emitEvent(DOMAIN_EDITOR_SAVE, {}, this);
            this.requestUpdate();
        } else {
            alert(t('domainEditor.nothingToSave'));
        }        
    }
    
    releaseChanges(e) {
        if (confirm(t('domainEditor.confirmRelease'))) {
            this.deleteFromStorage(this._checkoutPath, true);
        }         
    }
    
    async publishChanges(e) {
        if (!this.hasChanges()) {
            if (confirm(t('domainEditor.confirmPublish'))) {
                let payload = cloneDeep(this.domain);
                if (this._isTranslation) {
                    // Diff against the English file as published now, not as it
                    // was at checkout, so text English has since changed isn't
                    // mistaken for a translation.
                    const english = await fetchStorageJson(this._basePath);
                    const previous = await fetchStorageJson(this._publishPath);
                    if (!english) { alert(t('domainEditor.publishFailed')); return; }
                    const overlay = extractDomainOverlay(payload, english, previous);
                    overlay.published = payload.published || [];
                    overlay.editor = payload.editor;
                    overlay.checked_out = payload.checked_out;
                    payload = overlay;
                }
                if (!payload.published) {
                    payload.published = [];
                }
                let editlog = {};
                editlog.editor = payload.editor;
                editlog.checked_out = payload.checked_out;
                editlog.published = new Date();
                payload.published.push(editlog);
                delete payload.editor;
                delete payload.checked_out;
                delete payload.saved;
                this.putJSONInStorage(this._publishPath, payload, false, true);
            }
        } else {
            alert(t('domainEditor.saveFirst'));
        }        
    }
    
    
    constructor() {
        super(); 
        this.locale = 'en';
        this.loadJSONFromStorage = this.loadJSONFromStorage.bind(this);
        this.putJSONInStorage = this.putJSONInStorage.bind(this);
        this.deleteFromStorage = this.deleteFromStorage.bind(this);
        this.hasChanges = this.hasChanges.bind(this);
        this.cancelChanges = this.cancelChanges.bind(this);
        this.saveChanges = this.saveChanges.bind(this);
        this.releaseChanges = this.releaseChanges.bind(this);
        this.publishChanges = this.publishChanges.bind(this);
    }    
    
    static get properties() {
        return {
            user_profile: { type: Object },
            domain_name: { type: String },
            locale: { type: String },           // 'en' or a translation's code
            domain: { type: Object },
            original: { type: Object },
            editor: { type: Boolean }
        };
    }

    static get styles() {
        return css`
            .field_name {
                font-size: small;
                text-transform: uppercase;
                font-weight: bolder;
                margin-bottom: 10px;
            }
            .field_value {
                margin-bottom: 20px;
            }
            li {
                margin-bottom: 15px;
            }
        `;
    }
    
    render() {
        return html`
            <div style="margin: 20px;">
            ${this.user_profile && this.original ? html`
                ${this.domain ? html`
                
                    <div style="background-color: lightgrey; padding: 20px;">
                        ${t('domainEditor.intro')}
                    </div>
        
                    <div style="margin-top: 10px; background-color: #FF9999; padding: 20px;">
                        ${tHtml('domainEditor.warning')}
                    </div>        
                    
                    <h2>${t('profile.domainImageAlt', { domain: this.domain.name })}${this._isTranslation ? ` (${LOCALES[this.locale] ? LOCALES[this.locale].name : this.locale})` : ''}  <span style="font-size: small;">${t('domainEditor.editingBy', { domain: this.domain.name, editor: this.domain.editor, date: formatDate(this.domain.checked_out, { dateStyle: 'medium', timeStyle: 'short' }) })}</span></h2>
        
                    <div style="background-color: lightgrey; padding: 20px;">
        
                        <div class="field_name">${t('domainEditor.field.description')}</div>
                        <div class="field_value"> 
                           <intrivity-domain-editor-editable @change=${(e) => { this.domain.description = e.detail.value; }} .content=${this.domain.description}></intrivity-domain-editor-editable>
                        </div>

                        <div class="field_name">${t('domainEditor.field.domainFeedback')}</div>
                        <div class="field_value">
                            <ul>
                                <li>   
                                    <intrivity-domain-editor-editable @change=${(e) => { this.domain.feedback.a = e.detail.value; console.log(this.domain); }} .content=${this.domain.feedback.a}></intrivity-domain-editor-editable>
                                </li>
                                <li>   
                                    <intrivity-domain-editor-editable @change=${(e) => { this.domain.feedback.b = e.detail.value; console.log(this.domain); }} .content=${this.domain.feedback.b}></intrivity-domain-editor-editable>
                                </li>
                                <li>   
                                    <intrivity-domain-editor-editable @change=${(e) => { this.domain.feedback.c = e.detail.value; console.log(this.domain); }} .content=${this.domain.feedback.c}></intrivity-domain-editor-editable>
                                </li>
                                <li>   
                                    <intrivity-domain-editor-editable @change=${(e) => { this.domain.feedback.d = e.detail.value; console.log(this.domain); }} .content=${this.domain.feedback.d}></intrivity-domain-editor-editable>
                                </li>
                                <li>   
                                    <intrivity-domain-editor-editable @change=${(e) => { this.domain.feedback.e = e.detail.value; console.log(this.domain); }} .content=${this.domain.feedback.e}></intrivity-domain-editor-editable>
                                </li>        
                            </ul>
                        </div>
        
                    </div>
        
                    <h3>${this.domain.name} Domain Characteristics</h3>
        
                    ${this.domain.level_two.map((item) => html`
                        <div style="background-color: lightgrey; padding: 20px; margin-bottom: 20px;">
        
                            <div class="field_name">${t('domainEditor.field.characteristicName')}</div>
                            <div class="field_value"> 
                               <intrivity-domain-editor-editable @change=${(e) => { item.name = e.detail.value; }} .content=${item.name}></intrivity-domain-editor-editable>
                            </div>        

                            <div class="field_name">${t('domainEditor.field.description')}</div>
                            <div class="field_value"> 
                               <intrivity-domain-editor-editable @change=${(e) => { item.description = e.detail.value; }} .content=${item.description}></intrivity-domain-editor-editable>
                            </div> 
        
                            <div class="field_name">${t('domainEditor.field.likertLabels')}</div>
                            <div class="field_value"> 
                               Left: <intrivity-domain-editor-editable @change=${(e) => { item.left_label = e.detail.value; }} .content=${item.left_label}></intrivity-domain-editor-editable>
                            </div> 
                            <div class="field_value"> 
                               Right: <intrivity-domain-editor-editable @change=${(e) => { item.right_label = e.detail.value; }} .content=${item.right_label}></intrivity-domain-editor-editable>
                            </div>        
        
                            <div class="field_name">${t('domainEditor.field.characteristicFeedback')}</div>
                            <div class="field_value">
                                <ul>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { item.feedback.a = e.detail.value; console.log(this.domain); }} .content=${item.feedback.a}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { item.feedback.b = e.detail.value; console.log(this.domain); }} .content=${item.feedback.b}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { item.feedback.c = e.detail.value; console.log(this.domain); }} .content=${item.feedback.c}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { item.feedback.d = e.detail.value; console.log(this.domain); }} .content=${item.feedback.d}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { item.feedback.e = e.detail.value; console.log(this.domain); }} .content=${item.feedback.e}></intrivity-domain-editor-editable>
                                    </li>        
                                </ul>
                            </div>
                            
                            <div class="field_name">${t('domainEditor.field.characteristicBehaviors')}</div>
                            ${item.behaviors.map((behavior) => html`
                            
                                <div style="background-color: Gainsboro; padding: 20px; margin-bottom: 20px;">
        
                                    <div class="field_name">${t('domainEditor.field.behaviorName')}</div>
                                    <div class="field_value"> 
                                       <intrivity-domain-editor-editable @change=${(e) => { behavior.name = e.detail.value; }} .content=${behavior.name}></intrivity-domain-editor-editable>
                                    </div> 
        
                                    <div class="field_name">${t('domainEditor.field.behaviorQuote')}</div>
                                    <div class="field_value"> 
                                       <intrivity-domain-editor-editable @change=${(e) => { behavior.quote = e.detail.value; }} .content=${behavior.quote}></intrivity-domain-editor-editable>
                                    </div>        

                                    <div class="field_name">${t('domainEditor.field.behaviorDefinition')}</div>
                                    <div class="field_value"> 
                                       <intrivity-domain-editor-editable @change=${(e) => { behavior.definition = e.detail.value; }} .content=${behavior.definition}></intrivity-domain-editor-editable>
                                    </div>        

                                    <div class="field_name">${t('domainEditor.field.likertLabels')}</div>
                                    <div class="field_value"> 
                                       Left: <intrivity-domain-editor-editable @change=${(e) => { behavior.left_label = e.detail.value; }} .content=${behavior.left_label}></intrivity-domain-editor-editable>
                                    </div> 
                                    <div class="field_value"> 
                                       Right: <intrivity-domain-editor-editable @change=${(e) => { behavior.right_label = e.detail.value; }} .content=${behavior.right_label}></intrivity-domain-editor-editable>
                                    </div>        

                                    <div class="field_name">${t('domainEditor.field.behaviorSample')}</div>      
                                    <div class="field_value"> 
                                       <intrivity-domain-editor-editable @change=${(e) => { behavior.sample = e.detail.value; }} .content=${behavior.sample}></intrivity-domain-editor-editable>
                                    </div>

                                    <div class="field_name">${t('domainEditor.field.behaviorMeaning')}</div> 
                                    <div class="field_value">
                                        <ul>
                                            <li>   
                                                <intrivity-domain-editor-editable @change=${(e) => { behavior.meaning.a = e.detail.value; }} .content=${behavior.meaning.a}></intrivity-domain-editor-editable>
                                            </li>
                                            <li>   
                                                <intrivity-domain-editor-editable @change=${(e) => { behavior.meaning.b = e.detail.value; }} .content=${behavior.meaning.b}></intrivity-domain-editor-editable>
                                            </li>
                                            <li>   
                                                <intrivity-domain-editor-editable @change=${(e) => { behavior.meaning.c = e.detail.value; }} .content=${behavior.meaning.c}></intrivity-domain-editor-editable>
                                            </li>
                                            <li>   
                                                <intrivity-domain-editor-editable @change=${(e) => { behavior.meaning.d = e.detail.value; }} .content=${behavior.meaning.d}></intrivity-domain-editor-editable>
                                            </li>
                                            <li>   
                                                <intrivity-domain-editor-editable @change=${(e) => { behavior.meaning.e = e.detail.value; }} .content=${behavior.meaning.e}></intrivity-domain-editor-editable>
                                            </li>         
                                        </ul>
                                    </div>

                                    <div class="field_name">${t('domainEditor.field.behaviorStrengths')}</div> 
                                    <div class="field_value">
                                        <ul>
                                            <li>   
                                                <intrivity-domain-editor-editable @change=${(e) => { behavior.strengths.a = e.detail.value; }} .content=${behavior.strengths.a}></intrivity-domain-editor-editable>
                                            </li>
                                            <li>   
                                                <intrivity-domain-editor-editable @change=${(e) => { behavior.strengths.b = e.detail.value; }} .content=${behavior.strengths.b}></intrivity-domain-editor-editable>
                                            </li>
                                            <li>   
                                                <intrivity-domain-editor-editable @change=${(e) => { behavior.strengths.c = e.detail.value; }} .content=${behavior.strengths.c}></intrivity-domain-editor-editable>
                                            </li>
                                            <li>   
                                                <intrivity-domain-editor-editable @change=${(e) => { behavior.strengths.d = e.detail.value; }} .content=${behavior.strengths.d}></intrivity-domain-editor-editable>
                                            </li>
                                            <li>   
                                                <intrivity-domain-editor-editable @change=${(e) => { behavior.strengths.e = e.detail.value; }} .content=${behavior.strengths.e}></intrivity-domain-editor-editable>
                                            </li>         
                                        </ul>   
                                    </div>

                                    <div class="field_name">${t('domainEditor.field.behaviorRecommendations')}</div> 
                                    <div class="field_value">
                                        <ul>
                                            <li>   
                                                <intrivity-domain-editor-editable @change=${(e) => { behavior.recommendations.a = e.detail.value; }} .content=${behavior.recommendations.a}></intrivity-domain-editor-editable>
                                            </li>
                                            <li>   
                                                <intrivity-domain-editor-editable @change=${(e) => { behavior.recommendations.b = e.detail.value; }} .content=${behavior.recommendations.b}></intrivity-domain-editor-editable>
                                            </li>
                                            <li>   
                                                <intrivity-domain-editor-editable @change=${(e) => { behavior.recommendations.c = e.detail.value; }} .content=${behavior.recommendations.c}></intrivity-domain-editor-editable>
                                            </li>
                                            <li>   
                                                <intrivity-domain-editor-editable @change=${(e) => { behavior.recommendations.d = e.detail.value; }} .content=${behavior.recommendations.d}></intrivity-domain-editor-editable>
                                            </li>
                                            <li>   
                                                <intrivity-domain-editor-editable @change=${(e) => { behavior.recommendations.e = e.detail.value; }} .content=${behavior.recommendations.e}></intrivity-domain-editor-editable>
                                            </li>        
                                        </ul>  
                                    </div>
                                </div>
                            `)}
                            
                        </div>
                    `)}
        
                    <h3>${this.domain.name} Domain Attitudes</h3>
        
                    ${this.domain.attitudes.map((attitude) => html`
                            
                        <div style="background-color: Gainsboro; padding: 20px; margin-bottom: 20px;">

                            <div class="field_name">${t('domainEditor.field.attitudeName')}</div>
                            <div class="field_value"> 
                               <intrivity-domain-editor-editable @change=${(e) => { attitude.name = e.detail.value; }} .content=${attitude.name}></intrivity-domain-editor-editable>
                            </div> 

                            <div class="field_name">${t('domainEditor.field.attitudeQuote')}</div>
                            <div class="field_value"> 
                               <intrivity-domain-editor-editable @change=${(e) => { attitude.quote = e.detail.value; }} .content=${attitude.quote}></intrivity-domain-editor-editable>
                            </div>        

                            <div class="field_name">${t('domainEditor.field.attitudeDefinition')}</div>
                            <div class="field_value"> 
                               <intrivity-domain-editor-editable @change=${(e) => { attitude.definition = e.detail.value; }} .content=${attitude.definition}></intrivity-domain-editor-editable>
                            </div>        

                            <div class="field_name">${t('domainEditor.field.likertLabels')}</div>
                            <div class="field_value"> 
                               Left: <intrivity-domain-editor-editable @change=${(e) => { attitude.left_label = e.detail.value; }} .content=${attitude.left_label}></intrivity-domain-editor-editable>
                            </div> 
                            <div class="field_value"> 
                               Right: <intrivity-domain-editor-editable @change=${(e) => { attitude.right_label = e.detail.value; }} .content=${attitude.right_label}></intrivity-domain-editor-editable>
                            </div>        
  
                            <div class="field_name">${t('domainEditor.field.attitudeSample')}</div>      
                            <div class="field_value"> 
                               <intrivity-domain-editor-editable @change=${(e) => { attitude.sample = e.detail.value; }} .content=${attitude.sample}></intrivity-domain-editor-editable>
                            </div>

                            <div class="field_name">${t('domainEditor.field.attitudeMeaning')}</div> 
                            <div class="field_value">
                                <ul>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { attitude.meaning.a = e.detail.value; }} .content=${attitude.meaning.a}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { attitude.meaning.b = e.detail.value; }} .content=${attitude.meaning.b}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { attitude.meaning.c = e.detail.value; }} .content=${attitude.meaning.c}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { attitude.meaning.d = e.detail.value; }} .content=${attitude.meaning.d}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { attitude.meaning.e = e.detail.value; }} .content=${attitude.meaning.e}></intrivity-domain-editor-editable>
                                    </li>         
                                </ul>
                            </div>

                            <div class="field_name">${t('domainEditor.field.attitudeStrengths')}</div> 
                            <div class="field_value">
                                <ul>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { attitude.strengths.a = e.detail.value; }} .content=${attitude.strengths.a}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { attitude.strengths.b = e.detail.value; }} .content=${attitude.strengths.b}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { attitude.strengths.c = e.detail.value; }} .content=${attitude.strengths.c}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { attitude.strengths.d = e.detail.value; }} .content=${attitude.strengths.d}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { attitude.strengths.e = e.detail.value; }} .content=${attitude.strengths.e}></intrivity-domain-editor-editable>
                                    </li>         
                                </ul>   
                            </div>

                            <div class="field_name">${t('domainEditor.field.attitudeRecommendations')}</div> 
                            <div class="field_value">
                                <ul>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { attitude.recommendations.a = e.detail.value; }} .content=${attitude.recommendations.a}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { attitude.recommendations.b = e.detail.value; }} .content=${attitude.recommendations.b}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { attitude.recommendations.c = e.detail.value; }} .content=${attitude.recommendations.c}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { attitude.recommendations.d = e.detail.value; }} .content=${attitude.recommendations.d}></intrivity-domain-editor-editable>
                                    </li>
                                    <li>   
                                        <intrivity-domain-editor-editable @change=${(e) => { attitude.recommendations.e = e.detail.value; }} .content=${attitude.recommendations.e}></intrivity-domain-editor-editable>
                                    </li>        
                                </ul>  
                            </div>
                        </div>
                    `)}
        
                    <mwc-button outlined @click=${this.saveChanges}>${t('common.save')}</mwc-button>
                    <mwc-button outlined @click=${this.cancelChanges}>${t('common.cancel')}</mwc-button>
                    <mwc-button outlined @click=${this.publishChanges}>${t('domainEditor.publish')}</mwc-button>
                    <mwc-button outlined @click=${this.releaseChanges}>${t('domainEditor.release')}</mwc-button>
        
                    ${this.domain.published ? html`
                        <div style="margin: 10px; font-size: smaller;">
                            ${t('domainEditor.history')}
                            ${this.domain.published.map((history) => html`
                                <div sytle="font-size: smaller;">${t('domainEditor.historyEntry', { date: formatDate(history.published, { dateStyle: 'medium', timeStyle: 'short' }), editor: history.editor })}</div>
                            `)}
                        </div>
                    `: html``}
                    
                `: html`
                    <h2>${t('domainEditor.checkedOutBy', { editor: this.original.editor })}</h2>
                    <div>${t('domainEditor.checkedOutAt', { date: formatDate(this.original.checked_out, { dateStyle: 'medium', timeStyle: 'short' }) })}</div>
                `}
            `: html`
                ${this.editor ? html`
                    ${this.original ? html`
                        <h2>${t('profile.domainImageAlt', { domain: this.domain.name })}${this._isTranslation ? ` (${LOCALES[this.locale] ? LOCALES[this.locale].name : this.locale})` : ''}  <span style="font-size: small;">${t('domainEditor.editingBy', { domain: this.domain.name, editor: this.domain.editor, date: formatDate(this.domain.checked_out, { dateStyle: 'medium', timeStyle: 'short' }) })}</span></h2>
                    `: html`
                        <h2>${t('domainEditor.couldNotLoad')}</h2>
                    `}         
                `: html`
                    <h2>${t('common.loading')}</h2>
                `}
                
            `}
            </div>
        `
    } 
 
}
if (!customElements.get('intrivity-domain-editor')) customElements.define('intrivity-domain-editor', IntrivityDomainEditor);
export class IntrivityDomainEditorEditable extends Localized(LitElement) {
    
    constructor() {
        super();  
        this.cancelled = this.cancelled.bind(this);
        this.saved = this.saved.bind(this);
    }   
    
    static get properties() {
        return {
            content: { type: String, state: true },
            buffer: { type: String, state: true }
        };
    }

    connectedCallback() {
        super.connectedCallback();
        document.addEventListener(DOMAIN_EDITOR_CANCEL, this.cancelled);
        document.addEventListener(DOMAIN_EDITOR_SAVE, this.saved);
        document.addEventListener(DOMAIN_EDITOR_SAVE, this.saved);
        this.buffer = this.content;
    }
    
    disconnectedCallback() {
        document.removeEventListener(DOMAIN_EDITOR_CANCEL, this.cancelled);
        document.removeEventListener(DOMAIN_EDITOR_SAVE, this.saved);
        super.disconnectedCallback()
    }
    
    //firstUpdated() {
    //    this.buffer = this.content;
    //}

    static get styles() {
        return css`
            [contentEditable] {
                color: blue;
            }   
        `;
    }
    
    cancelled(e) {
        this.shadowRoot.getElementById('target').textContent = this.content;
        this.requestUpdate();
    }
    
    saved(e) {
        this.requestUpdate();
    }    
    
    render() {
        return html`
            <span id="target" @blur=${(e) => {
            emitEvent('change', { value: this.buffer }, this);
        }} @input=${(e)=> {
            this.buffer = this.shadowRoot.getElementById('target').textContent;
            this.requestUpdate();
        }} contentEditable="true">${this.content ? this.content: '[No content]'}</span>`;
    } 
 
}
if (!customElements.get('intrivity-domain-editor-editable')) customElements.define('intrivity-domain-editor-editable', IntrivityDomainEditorEditable);

